import { useState } from 'react'
import { Clock, Circle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Label } from '@/components/ui/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'

interface AvailabilityCardProps {
  autoAwayMinutes: number | null
  onAutoAwayChange: (minutes: number | null) => void
}

// Aligned with PresenceConfig::Agent::AUTO_AWAY_DEFAULT (10 minutes)
const AUTO_AWAY_OPTIONS = [
  { label: 'Disabled', value: 'disabled' },
  { label: '5 minutes', value: '5' },
  { label: '10 minutes (default)', value: '10' },
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '1 hour', value: '60' },
  { label: '2 hours', value: '120' },
]

export function AvailabilityCard({ autoAwayMinutes, onAutoAwayChange }: AvailabilityCardProps) {
  const [isUpdating, setIsUpdating] = useState(false)

  const currentValue = autoAwayMinutes !== null ? String(autoAwayMinutes) : 'disabled'

  const handleChange = async (value: string) => {
    setIsUpdating(true)
    const minutes = value === 'disabled' ? null : parseInt(value, 10)

    try {
      const response = await fetch('/profile/auto-away', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ auto_away_minutes: minutes }),
      })

      if (response.ok) {
        onAutoAwayChange(minutes)
      }
    } catch (error) {
      console.error('Failed to update auto-away setting:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="size-5 text-muted-foreground" />
          <div>
            <CardTitle>Availability Settings</CardTitle>
            <CardDescription>Configure when you appear available to visitors</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            Auto-away timeout
          </Label>
          <p className="text-sm text-muted-foreground">
            Automatically set your status to "Away" after being inactive for this duration.
          </p>
          <Select value={currentValue} onValueChange={handleChange} disabled={isUpdating}>
            <SelectTrigger className="w-full max-w-xs cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTO_AWAY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium">Status indicators</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Circle className="size-3 fill-current text-emerald-500" />
              <span className="text-sm"><strong>Online</strong> - Available and actively using the app</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="size-3 fill-current text-amber-500" />
              <span className="text-sm"><strong>Away</strong> - Temporarily unavailable or idle</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="size-3 fill-current text-gray-400" />
              <span className="text-sm"><strong>Offline</strong> - Not receiving chat notifications</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
